import { useCallback, useEffect, useMemo, useState } from 'react'

import { formatDate } from 'react-day-picker/moment'

import axios from 'axios'

import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import * as ValidationRules from 'Pods/Shared/Rules/Rules'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

const JOB_TYPE = 'public'
const META_SOURCE = 'ai_studio_assist'
const DATE_FORMAT = 'YYYY-MM-DD'

export const BUDGET_MIN = 5
export const BUDGET_MAX = 1000000
export const BUDGET_DEFAULT = 10

const INITIAL_DATA = {
  title: '',
  description: '',
  software: [],
  deadline: '',
  isEndless: false,
  budget: '',
  resell: false,
  terms: false,
  references: [],
}

const flashError = message => App.events.publish('app:notification:flash:error', message)

export default function useAssistForm(onSuccess) {
  const t = useTranslation()
  const [data, setData] = useState(INITIAL_DATA)
  const [availableSoftware, setAvailableSoftware] = useState([])
  const [showErrors, setShowErrors] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    axios.get('/api/internal/jobs/software')
      .then(response => setAvailableSoftware(response.data))
      .catch(() => flashError(t('viewerSidebar.enhance.assist.form.loadSoftwareError')))
  }, [t])

  const softwareOptions = useMemo(
    () => availableSoftware.map(item => ({ value: item.id, label: item.text })),
    [availableSoftware],
  )

  const fieldValidations = () => {
    const rules = [
      RuleRunner(
        'title', 'Title',
        ValidationRules.required, ValidationRules.onlyLatinSymbols, ValidationRules.isLimitedLength,
      ),
      RuleRunner('description', 'Description', ValidationRules.required, ValidationRules.noEmoji),
      RuleRunner('software', '3D Software', ValidationRules.required),
      RuleRunner(
        'budget', 'Budget',
        ValidationRules.min(BUDGET_MIN), ValidationRules.max(BUDGET_MAX), ValidationRules.required,
      ),
    ]

    if (!data.isEndless) {
      rules.push(RuleRunner('deadline', 'Deadline', ValidationRules.required))
    }

    return rules
  }

  const validationErrors = run(data, fieldValidations())
  const errorFor = field => (showErrors ? validationErrors[field] || '' : '')

  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target

    setData(prev => ({ ...prev, [name]: value }))
  }, [])

  const handleDescriptionChange = useCallback((value) => {
    setData(prev => ({ ...prev, description: value }))
  }, [])

  const handleSoftwareChange = useCallback((ids) => {
    setData(prev => ({ ...prev, software: ids }))
  }, [])

  const handleDayChange = useCallback((value = '') => {
    setData(prev => ({ ...prev, deadline: value, isEndless: false }))
  }, [])

  const handleEndlessChange = useCallback((value) => {
    setData(prev => ({ ...prev, isEndless: value, deadline: value ? '' : prev.deadline }))
  }, [])

  const handleReferencesChange = useCallback((references) => {
    setData(prev => ({ ...prev, references }))
  }, [])

  const handleResellChange = useCallback((value) => {
    setData(prev => ({ ...prev, resell: value }))
  }, [])

  const handleTermsChange = useCallback((value) => {
    setData(prev => ({ ...prev, terms: value }))
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    setShowErrors(true)

    if (Object.keys(run(data, fieldValidations())).length > 0) return

    setIsSubmitting(true)

    const params = {
      data: {
        attributes: {
          type: JOB_TYPE,
          title: data.title,
          description: data.description,
          budget: data.budget,
          deadline: data.isEndless || !data.deadline ? '' : formatDate(data.deadline, DATE_FORMAT),
          agreed_to_terms_of_use: data.terms,
          release_ip_rights: data.resell,
          file_types: data.software,
          endless: data.isEndless,
          job_attachments: data.references,
          meta_source: META_SOURCE,
        },
      },
    }

    axios.post('/api/internal/jobs', params)
      .then(() => onSuccess())
      .catch((xhr) => {
        setIsSubmitting(false)
        const message = xhr.response?.data?.errors?.[0] || t('viewerSidebar.enhance.assist.form.submitError')
        flashError(message)
      })
  }

  return {
    data,
    softwareOptions,
    showErrors,
    isSubmitting,
    errorFor,
    handleFieldChange,
    handleDescriptionChange,
    handleSoftwareChange,
    handleDayChange,
    handleEndlessChange,
    handleReferencesChange,
    handleResellChange,
    handleTermsChange,
    handleSubmit,
  }
}
